"use client";

import { useState, useEffect, useMemo, useCallback } from "react";
import { Container, Row, Col, Alert, Spinner } from "react-bootstrap";
import NavbarSection from "@/app/components/NavbarSection";
import FooterSection from "@/app/components/FooterSection";
import { BlogCard } from "@/app/components/blog/BlogCard";
import { FeaturedBlogCard } from "@/app/components/blog/FeaturedBlogCard";
import { BlogSidebar } from "@/app/components/blog/BlogSidebar";
import { Pagination } from "@/app/components/blog/Pagination";
import {
  fetchBlogPosts,
  fetchBlogCategorySummary,
  fetchPopularPosts,
  getPopularPosts,
} from "@/app/services/blogApi";
import { BlogPost, BlogCategory } from "@/app/types/blog";
import { useToast } from "@/app/hooks/use-toast";
import MakeWebsiteLiveSection from "../components/blog/MakeWebsiteLiveSection";
import BlogSearchBar from "../components/blog/BlogSearchBar";
import Link from "next/link";


// import BlogAd from "../../../public/aiimg/blgbnr.jpg"
import BlogAd0 from "../../../public/aiimg/blgbnr0.png"

import Image from "next/image";

const FIRST_PAGE_POSTS = 11;
const DEFAULT_POSTS_PER_PAGE = 10;

const computeTotalPages = (total: number): number => {
  if (total <= 0) return 1;
  if (total <= FIRST_PAGE_POSTS) return 1;
  const remaining = Math.max(0, total - FIRST_PAGE_POSTS);
  return 1 + Math.ceil(remaining / DEFAULT_POSTS_PER_PAGE);
};

const ManagedCloudPage = () => {
  const [selectedCategory, setSelectedCategory] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [blogCategories, setBlogCategories] = useState<BlogCategory[]>([]);
  const [popularPosts, setPopularPosts] = useState<BlogPost[]>([]);
  const [totalPosts, setTotalPosts] = useState(0);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<BlogPost[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchLoadingMore, setSearchLoadingMore] = useState(false);
  const [searchPage, setSearchPage] = useState(1);
  const [searchTotalPages, setSearchTotalPages] = useState(1);

  const { toast } = useToast();

  const selectedCategorySlug = useMemo(() => {
    if (!selectedCategory) return "";
    const match = blogCategories.find(
      (c) => c.name === selectedCategory || c.slug === selectedCategory
    );
    return match?.slug ?? "";
  }, [selectedCategory, blogCategories]);

  /* ---------- Load Categories + Popular ---------- */
  useEffect(() => {
    let cancelled = false;

    const loadMeta = async () => {
      try {
        const [categorySummary, popular] = await Promise.all([
          fetchBlogCategorySummary(),
          fetchPopularPosts(7),
        ]);

        if (cancelled) return;

        setBlogCategories(categorySummary.categories || []);
        setTotalPosts(categorySummary.total || 0);
        setPopularPosts(popular || []);
      } catch (err) {
        console.error("Failed to load blog metadata", err);
      }
    };

    loadMeta();
    return () => {
      cancelled = true;
    };
  }, []);

  /* ---------- Search ---------- */
  useEffect(() => {
    let cancelled = false;
    const query = searchQuery.trim();

    if (query.length < 2) {
      setSearchResults([]);
      setSearchPage(1);
      setSearchTotalPages(1);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    setSearchPage(1);

    const timeout = setTimeout(async () => {
      try {
        const res = await fetchBlogPosts({
          page: 1,
          perPage: 10,
          search: query,
        });

        if (!cancelled) {
          setSearchResults(res.posts);
          setSearchTotalPages(
            res.totalPages ?? Math.ceil((res.total || 0) / 10)
          );
        }
      } catch {
        if (!cancelled) {
          setSearchResults([]);
          setSearchTotalPages(1);
        }
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [searchQuery]);

  const handleLoadMoreSearchResults = useCallback(async () => {
    if (
      searchLoadingMore ||
      searchPage >= searchTotalPages ||
      searchQuery.trim().length < 2
    )
      return;

    const nextPage = searchPage + 1;
    setSearchLoadingMore(true);

    try {
      const res = await fetchBlogPosts({
        page: nextPage,
        perPage: 10,
        search: searchQuery.trim(),
      });

      setSearchResults((prev) => [...prev, ...res.posts]);
      setSearchPage(nextPage);
    } catch {
      toast({
        title: "Error",
        description: "Failed to load more results.",
        variant: "destructive",
      });
    } finally {
      setSearchLoadingMore(false);
    }
  }, [
    searchLoadingMore,
    searchPage,
    searchTotalPages,
    searchQuery,
    toast,
  ]);

  /* ---------- Load Blog Posts ---------- */
  useEffect(() => {
    let cancelled = false;

    const loadPosts = async () => {
      try {
        setLoading(true);

        const perPage =
          currentPage === 1 ? FIRST_PAGE_POSTS : DEFAULT_POSTS_PER_PAGE;

        const res = await fetchBlogPosts({
          page: currentPage,
          perPage,
          category: selectedCategorySlug || undefined,
        });

        if (cancelled) return;

        setBlogPosts(res.posts);
        setTotalPosts(res.total ?? res.posts.length);
        setTotalPages(res.totalPages ?? computeTotalPages(res.total || 0));

        if (!popularPosts.length) {
          setPopularPosts(getPopularPosts(res.posts, 7));
        }
      } catch {
        toast({
          title: "Error",
          description: "Failed to load blog posts.",
          variant: "destructive",
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPosts();
    return () => {
      cancelled = true;
    };
  }, [currentPage, selectedCategorySlug, toast, popularPosts.length]);

  const featuredPost = currentPage === 1 ? blogPosts[0] : undefined;
  const listPosts =
    currentPage === 1 ? blogPosts.slice(1) : blogPosts;

  const handlePageChange = (page: number) => {
    if (page === currentPage) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading && blogPosts.length === 0) {
    return (
      <>
        <NavbarSection />
        <Container className="py-5 text-center">
          <Spinner animation="border" />
        </Container>
        <FooterSection />
      </>
    );
  }

  return (
    <>
      <NavbarSection />

      <div className="min-vh-100 bg-light py-5">
        <Container>
          <div className="mb-5 text-center">
            <h1 className="display-5 fw-bold">Latest from Blogs</h1>
            <p className="text-muted fs-5">
              Blogs, Insights, Tutorials & Industry News
            </p>
          </div>

          <BlogSearchBar
            onSearch={setSearchQuery}
            results={searchResults}
            isLoading={searchLoading}
            isLoadingMore={searchLoadingMore}
            hasMore={searchPage < searchTotalPages}
            onLoadMore={handleLoadMoreSearchResults}
            placeholder="Search articles..."
          />

          {/* {featuredPost && (
            <Row className="mb-5">
              <Col lg={8}>
                <div className="mb-4">
                <FeaturedBlogCard post={featuredPost} />
                </div>
              </Col> 
              <Col lg={4}>

                <Link href="/dedicated-servers-usa"  className="d-block text-center" target="_blank">
        <Image src={Server} width={300} height={500} alt="..."  className="mx-auto text-center rounded-4"  />
         
       </Link>
              </Col>
            </Row>
          )} */}

          {selectedCategory && (
            <Alert variant="info" className="mb-4">
              Showing posts in <strong>{selectedCategory}</strong> ({totalPosts})
            </Alert>
          )}

<Row className="g-4">
  {/* LEFT COLUMN */}
  <Col lg={8}>
    {/* FEATURED POST (ONLY ON PAGE 1) */}
    {featuredPost && currentPage === 1 && (
      <div className="mb-4">
        <FeaturedBlogCard post={featuredPost} />
      </div>
    )}

    {/* BLOG GRID */}
    <Row xs={1} md={2} className="g-4">
      {listPosts.map((post, idx) => (
        <Col key={post.id}>
          <BlogCard post={post} index={idx} />
        </Col>
      ))}
    </Row>

    {/* PAGINATION */}
    {totalPages > 1 && (
      <div className="mt-5">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </div>
    )}
  </Col>

  {/* RIGHT SIDEBAR */}
  <Col lg={4}>
    <div className="sticky-top" style={{ top: "1rem" }}>
 

      <BlogSidebar
        categories={blogCategories}
        popularPosts={popularPosts}
        selectedCategory={selectedCategory}
        totalCount={totalPosts}
        onCategorySelect={(c) => {
          setSelectedCategory(c);
          setCurrentPage(1);
        }}
      />
    </div>
  </Col>
</Row>
        <div className="my-5">
         <Link href="/linux-shared-hosting-usa"> <Image src={BlogAd0} width={1400} alt="..." className="img-fluid rounded-5"/></Link>
        </div>
          {/* <MakeWebsiteLiveSection /> */}
        </Container>
      </div>

      <FooterSection />
    </>
  );
};

export default ManagedCloudPage;
